import { HttpResponseException } from '@monorepo/services';
import {
  TBulkCountAppBackgroundDB,
  TBulkCountAppEventWithStatus,
  TBulkCountAppUserActualsDB,
  TBulkCountSyncEventsDTO,
  TEventBctxDetails,
  TRESPONSE_WashReceiveRequest,
  TRESPONSE_WashSendRequest,
  TSizeQuantity,
  TSyncEventStatus,
  TBulkCountAppSizeMap,
  TSizeToUserDBActualMapItem,
  TSizeToUserDBWipMapItem,
  TBgSizeToActualMapItem,
  TBgSizeToWipMapItem,
  TSizeToSavedCountMap,
  TRESPONSE_BusinessContextData,
  TBCTXKeysWithoutSize,
  TBulkCountSizeMapItemType,
  TBulkCountUserDBLineMapItem,
  TBctxAndVendorWipMapItem,
  TBctxAndVendorKeyToBctxWithoutSizeAndSizeListMap,
  TBulkCountUserDBLineMap,
  TSizeToWipMapItem,
  TBulkCountUserDBShiftMapItem,
  TSelectedStationIdAndShiftId,
  TBctxItemWithoutSize,
  TVendorDTO,
  TRESPONSE_WashingReceiveBctxDataResponse,
  TBulkCountUserDBShiftSizeMapItem,
  TBulkCountBgDBLineMap,
  TBctxAndVendorWipMap,
  TBulkCountPendingTransactions,
  TBulkCountBgDBShiftMap,
  TBulkCountUserDBShiftMap,
  TBulkCountEvent,
  TBulkWashingEventType,
  TBulkCountPendingTransactionWithBctxList,
} from '@monorepo/types';
import produce from 'immer';
import { Observable, of } from 'rxjs';
import { BCTXComputationHelper } from '../bctx-computation-helper';

export const BulkCountAppComputationHelper = {
  dashFormateDate: (eventTime: number) => {
    const day = new Date(eventTime);
    const year = day.getFullYear();
    const month = day.getMonth() + 1 < 10 ? '0' + (day.getMonth() + 1) : day.getMonth() + 1;
    const date = day.getDate().toString().length < 2 ? '0' + day.getDate() : day.getDate();
    return year + '-' + month + '-' + date;
  },
  getOrderedBctx: (bctx: TBctxItemWithoutSize[], order: TBCTXKeysWithoutSize[]) => {
    return order.reduce((acc, curr) => {
      const item = bctx.find((item) => item.k === curr);
      if (item) acc.push(item);
      return acc;
    }, [] as TBctxItemWithoutSize[]);
  },
  getUniqueBctxKeyAndVendorIdWithOrder(
    bctxKeysOrder: TBCTXKeysWithoutSize[],
    bctxItemListWithoutSize: TBctxItemWithoutSize[],
    vendorId: TVendorDTO['vendorId'] | undefined,
  ) {
    return (
      BCTXComputationHelper.getUniqueBctxKeyWithOrder(bctxKeysOrder, bctxItemListWithoutSize) +
      (vendorId ? '-' + vendorId : '')
    );
  },
  getEventsWithStatus(
    userDbEvents: TBulkCountAppUserActualsDB['dailyCache']['events']['list'],
    bgDbEvents: TBulkCountAppBackgroundDB['dailyCache']['events']['list'],
    retryCount: number,
  ) {
    const bgDbEventsLength = bgDbEvents.length;
    const events: TBulkCountAppEventWithStatus[] = userDbEvents.map((event, index) => {
      if (index < bgDbEventsLength)
        return {
          event,
          backgroundMetadata: bgDbEvents[index],
        };
      else
        return {
          event,
          backgroundMetadata: {
            remainingRetriesCount: retryCount,
            eventStatus: 'YET_TO_BE_SYNCED',
            errorDetails: {},
          },
        };
    });
    return events;
  },
  getLastSyncedIndex(events: TBulkCountAppEventWithStatus[]) {
    for (let index = 0; index < events.length; index++) {
      if (events[index].backgroundMetadata.eventStatus === 'YET_TO_BE_SYNCED') return index - 1;
    }
    return events.length - 1;
  },
  getUpdatedEventStatusAfterSaveAndFailedEventIndices(
    startIndex: number,
    events: TBulkCountAppEventWithStatus[],
    failedEventsIndices: number[],
    saveEventStatuses: TRESPONSE_WashSendRequest[] | TRESPONSE_WashReceiveRequest[],
  ) {
    saveEventStatuses.forEach((saveEventStatus, index) => {
      events = produce(events, (draft) => {
        if (saveEventStatus.status === 'SUCCESS') {
          draft[index + startIndex].backgroundMetadata.eventStatus = 'SYNCED';
          draft[index + startIndex].backgroundMetadata.errorDetails = {};
        } else {
          failedEventsIndices.push(index + startIndex);
          draft[index + startIndex].backgroundMetadata.eventStatus = 'VALIDATION_ERROR';
          draft[index + startIndex].backgroundMetadata.errorDetails = {};
        }
      });
    });
    return [events, failedEventsIndices] as const;
  },
  getBulkCountSyncEventsDTOAfterServerError(
    error: HttpResponseException,
    lastSyncedIndex: number,
    batchCount: number,
    failedEventsIndices: number[],
    events: TBulkCountAppEventWithStatus[],
    toBeSyncedEvents: TBulkCountAppEventWithStatus[],
  ): Observable<TBulkCountSyncEventsDTO> {
    const status = error && error.httpResponse && error.httpResponse.status ? error.httpResponse.status : 500;
    switch (status) {
      case 500: {
        for (let index = lastSyncedIndex + 1; index <= lastSyncedIndex + toBeSyncedEvents.length; index++) {
          if (events[index].backgroundMetadata.remainingRetriesCount <= 0) {
            failedEventsIndices.push(index);
            events = produce(events, (draft) => {
              draft[index].backgroundMetadata.eventStatus = 'SERVER_ERROR';
              draft[index].backgroundMetadata.errorDetails = {};
            });
          } else {
            events = produce(events, (draft) => {
              draft[index].backgroundMetadata.remainingRetriesCount--;
            });
          }
        }
        return of({
          events,
          failedEventsIndices,
          lastBatchSyncStatus: 'SERVER_ERROR' as TSyncEventStatus,
        });
      }
      // Cross Origin issue network status will return 0
      case 0: {
        return of({
          events,
          failedEventsIndices,
          lastBatchSyncStatus: 'NO_INTERNET' as TSyncEventStatus,
        });
      }
      default: {
        return of({
          events,
          failedEventsIndices,
          lastBatchSyncStatus: 'UNKNOWN_ERROR' as TSyncEventStatus,
        });
      }
    }
  },
  backFillBctxInBgLineMap(
    eventMetadata: TBulkCountAppEventWithStatus['event']['metaData'],
    bgLineMap: TBulkCountAppBackgroundDB['dailyCache']['lineMap'],
    sizes: string[],
  ) {
    const { bctxKey, bctxWithoutSize, stationDetails } = eventMetadata;
    const stationId = stationDetails.subject.subject;
    const shift = stationDetails.shift.id;
    sizes.map((size) => {
      bgLineMap = produce(bgLineMap, (bgLineMapDraft) => {
        if (!bgLineMapDraft[stationId]) {
          bgLineMapDraft[stationId] = {
            shiftMap: {},
            bctxAndVendorWipMap: {},
          };
        }
        if (!bgLineMapDraft[stationId].bctxAndVendorWipMap[bctxKey]) {
          bgLineMapDraft[stationId].bctxAndVendorWipMap[bctxKey] = {
            bctxItemListWithoutSize: eventMetadata.bctxWithoutSize,
            sizeMap: {},
            bctxKeyAndVendorId: bctxKey,
          };
        }

        if (!bgLineMapDraft[stationId].shiftMap[shift]) {
          bgLineMapDraft[stationId].shiftMap[shift] = {};
        }
        if (!bgLineMapDraft[stationId].shiftMap[shift][bctxKey]) {
          bgLineMapDraft[stationId].shiftMap[shift][bctxKey] = {
            bctxItemListWithoutSize: bctxWithoutSize,
            sizeMap: {},
            bctxKeyAndVendorId: bctxKey,
          };
        }

        if (!bgLineMapDraft[stationId].bctxAndVendorWipMap[bctxKey].sizeMap[size]) {
          bgLineMapDraft[stationId].bctxAndVendorWipMap[bctxKey].sizeMap[size] = {
            name: size,
            wipQty: 0,
          };
        }
        if (!bgLineMapDraft[stationId].shiftMap[shift][bctxKey].sizeMap[size]) {
          bgLineMapDraft[stationId].shiftMap[shift][bctxKey].sizeMap[size] = {
            name: size,
            actuals: 0,
          };
        }
      });
    });
    return bgLineMap;
  },
  backFillBctxInUserLineMap(
    businessContextDetailsWithoutSize: TBctxItemWithoutSize[],
    size: string,
    bctxKey: string,
    userDBLineMap: TBulkCountAppUserActualsDB['dailyCache']['lineMap'],
    stationId: string,
    shift: string,
  ) {
    userDBLineMap = produce(userDBLineMap, (userDBLineMapDraft) => {
      if (!userDBLineMapDraft[stationId]) {
        userDBLineMapDraft[stationId] = {
          shiftMap: {
            [shift]: {},
          },
          bctxAndVendorWipMap: {},
        };
      } else if (!userDBLineMapDraft[stationId].shiftMap[shift]) {
        userDBLineMapDraft[stationId].shiftMap[shift] = {};
      }
    });
    userDBLineMap = produce(userDBLineMap, (userDBLineMapDraft) => {
      if (!userDBLineMapDraft[stationId].bctxAndVendorWipMap[bctxKey]) {
        userDBLineMapDraft[stationId].bctxAndVendorWipMap[bctxKey] = {
          bctxItemListWithoutSize: businessContextDetailsWithoutSize,
          sizeMap: {
            [size]: {
              name: size,
              wipQty: 0,
            },
          },
          bctxKeyAndVendorId: bctxKey,
        };
      } else if (!userDBLineMapDraft[stationId].bctxAndVendorWipMap[bctxKey].sizeMap[size]) {
        userDBLineMapDraft[stationId].bctxAndVendorWipMap[bctxKey].sizeMap[size] = {
          name: size,
          wipQty: 0,
        };
      }
    });
    return produce(userDBLineMap, (userDBLineMapDraft) => {
      if (!userDBLineMapDraft[stationId].shiftMap[shift][bctxKey]) {
        userDBLineMapDraft[stationId].shiftMap[shift][bctxKey] = {
          sizeMap: {
            [size]: {
              name: size,
              totalCompletedCount: 0,
              sentQty: 0,
            },
          },
          bctxKeyAndVendorId: bctxKey,
        };
      } else if (!userDBLineMapDraft[stationId].shiftMap[shift][bctxKey].sizeMap[size]) {
        userDBLineMapDraft[stationId].shiftMap[shift][bctxKey].sizeMap[size] = {
          name: size,
          totalCompletedCount: 0,
          sentQty: 0,
        };
      }
    });
  },
  updateWipAndActualsForFailedEvents(
    bgDB: TBulkCountAppBackgroundDB,
    failedEvents: TBulkCountAppEventWithStatus[],
    eventType: TBulkWashingEventType,
  ) {
    failedEvents.forEach((failedEvent) => {
      if (
        failedEvent.backgroundMetadata.eventStatus === 'VALIDATION_ERROR' ||
        failedEvent.backgroundMetadata.eventStatus === 'SERVER_ERROR'
      ) {
        const stationDetails = failedEvent.event.metaData.stationDetails;
        const updatedLineMap = BulkCountAppComputationHelper.backFillBctxInBgLineMap(
          failedEvent.event.metaData,
          bgDB.dailyCache.lineMap,
          failedEvent.event.sizeQuantities.map((sizeQuantity) => sizeQuantity.size),
        );
        bgDB = produce(bgDB, (draft) => {
          draft.dailyCache.lineMap = updatedLineMap;
        });
        failedEvent.event.sizeQuantities.forEach((item) => {
          bgDB = produce(bgDB, (bgDBDraft) => {
            const actualMap =
              bgDBDraft.dailyCache.lineMap[stationDetails.subject.subject].shiftMap[stationDetails.shift.id];
            const wipMap = bgDBDraft.dailyCache.lineMap[stationDetails.subject.subject].bctxAndVendorWipMap;
            if (eventType === 'SEND')
              wipMap[failedEvent.event.metaData.bctxKey].sizeMap[item.size].wipQty += item.quantity;
            actualMap[failedEvent.event.metaData.bctxKey].sizeMap[item.size].actuals -= item.quantity;
            bgDBDraft.dailyCache.totalCount -= item.quantity;
          });
        });
      }
    });
    return bgDB;
  },
  mergeUserLineMapWithBgLineMap(
    userLineMap: TBulkCountUserDBLineMap,
    bgLineMap: TBulkCountBgDBLineMap,
  ): TBulkCountUserDBLineMap {
    Object.entries(bgLineMap).forEach(([line, bgLineMapItem]) => {
      Object.entries(bgLineMapItem.bctxAndVendorWipMap).forEach(([bctxKey, wipMapItem]) => {
        Object.entries(wipMapItem.sizeMap).forEach(([size, sizeInfo]) => {
          userLineMap = produce(userLineMap, (draft) => {
            const userLineSizeInfo = draft[line].bctxAndVendorWipMap[bctxKey].sizeMap[size];
            userLineSizeInfo.wipQty += sizeInfo.wipQty;
          });
        });
      });
      Object.entries(bgLineMapItem.shiftMap).forEach(([shift, shiftMapItem]) => {
        Object.entries(shiftMapItem).forEach(([bctxKey, shiftMapItem]) => {
          Object.entries(shiftMapItem.sizeMap).forEach(([size, sizeInfo]) => {
            userLineMap = produce(userLineMap, (draft) => {
              const userLineSizeInfo = draft[line].shiftMap[shift][bctxKey].sizeMap[size];
              userLineSizeInfo.totalCompletedCount += sizeInfo.actuals;
            });
          });
        });
      });
    });
    return userLineMap;
  },
  createInitialShiftMapItem: (
    bctxWipMap: TBctxAndVendorWipMap,
    shiftMap: TBulkCountUserDBShiftMapItem,
    eventType: TBulkWashingEventType,
  ) => {
    const shiftMapItem: TBulkCountUserDBShiftMapItem = {};
    Object.entries(bctxWipMap).forEach(([bctx_key, bctxWipMapItem]) => {
      shiftMapItem[bctx_key] = {
        bctxKeyAndVendorId: bctx_key,
        sizeMap: Object.entries(bctxWipMapItem.sizeMap).reduce((acc, [size, _]) => {
          acc[size] = {
            name: size,
            sentQty: eventType === 'RECEIVE' ? shiftMap[bctx_key].sizeMap[size].sentQty : 0,
            totalCompletedCount: eventType === 'RECEIVE' ? shiftMap[bctx_key].sizeMap[size].totalCompletedCount : 0,
          };
          return acc;
        }, {} as TBulkCountUserDBShiftSizeMapItem['sizeMap']),
      };
    });
    return shiftMapItem;
  },
  removeAllBctxApartFromPendingTransactionsAndZeroWip(
    lineMap: TBulkCountUserDBLineMap,
    pendingTransactions: TBulkCountPendingTransactions,
    type: TBulkWashingEventType,
  ) {
    let updatedLineMap: TBulkCountUserDBLineMap = {};
    Object.entries(lineMap).forEach(([line, lineMapItem]) => {
      const bctxWipMap = Object.entries(lineMapItem.bctxAndVendorWipMap).reduce((wipAcc, [bctxKey, wipMapItem]) => {
        const pendingTransaction = pendingTransactions.find(
          (pendingTransaction) => pendingTransaction.bctxKey === bctxKey && pendingTransaction.lineId === line,
        );
        Object.entries(wipMapItem.sizeMap).forEach(([size, sizeInfo]) => {
          if (
            (pendingTransaction && Object.keys(pendingTransaction.sizeMap).find((savedSize) => savedSize === size)) ||
            sizeInfo.wipQty > 0
          ) {
            if (!wipAcc[bctxKey]) {
              wipAcc[bctxKey] = {
                bctxItemListWithoutSize: wipMapItem.bctxItemListWithoutSize,
                bctxKeyAndVendorId: wipMapItem.bctxKeyAndVendorId,
                sizeMap: {},
              };
            }
            wipAcc[bctxKey].sizeMap[size] = sizeInfo;
          }
        });
        return wipAcc;
      }, {} as TBctxAndVendorWipMap);

      if (Object.keys(bctxWipMap).length > 0) {
        const updatedShiftMap = Object.keys(lineMapItem.shiftMap).reduce((acc, shift) => {
          acc[shift] = BulkCountAppComputationHelper.createInitialShiftMapItem(
            bctxWipMap,
            lineMapItem.shiftMap[shift],
            type,
          );
          return acc;
        }, {} as TBulkCountUserDBShiftMap);
        updatedLineMap = produce(updatedLineMap, (draft) => {
          draft[line] = {
            bctxAndVendorWipMap: bctxWipMap,
            shiftMap: updatedShiftMap,
          };
        });
      }
    });
    return updatedLineMap;
  },
  createInitialBulkCountSendAppLineMapItem: (
    businessContextData: TRESPONSE_BusinessContextData,
    shiftId: string,
    bctxKeysOrder: TBCTXKeysWithoutSize[],
  ): TBulkCountUserDBLineMapItem => {
    const bctxAndVendorWipMap: TBulkCountUserDBLineMapItem['bctxAndVendorWipMap'] = {};
    const shiftMap: TBulkCountUserDBLineMapItem['shiftMap'] = {
      [shiftId]: {},
    };
    const bctxKeyToBctxWithoutSizeAndSizeListMap: TBctxAndVendorKeyToBctxWithoutSizeAndSizeListMap = {};
    businessContextData.forEach((data) => {
      const bctxItemListWithoutSize = BCTXComputationHelper.filterBctxItemsWithKeys(bctxKeysOrder, data.bctx_items);
      const size = BCTXComputationHelper.getSizeFromBctxItems(data.bctx_items).v;
      const bctx_key = BulkCountAppComputationHelper.getUniqueBctxKeyAndVendorIdWithOrder(
        bctxKeysOrder,
        bctxItemListWithoutSize,
        undefined,
      );
      if (!bctxKeyToBctxWithoutSizeAndSizeListMap[bctx_key]) {
        bctxKeyToBctxWithoutSizeAndSizeListMap[bctx_key] = {
          bctxItemListWithoutSize,
          sizes: [size],
        };
      } else bctxKeyToBctxWithoutSizeAndSizeListMap[bctx_key].sizes.push(size);
    });

    Object.entries(bctxKeyToBctxWithoutSizeAndSizeListMap).forEach(
      ([bctxKey, bctxKeyToBctxWithoutSizeAndSizeListMapItem]) => {
        bctxAndVendorWipMap[bctxKey] = {
          bctxItemListWithoutSize: bctxKeyToBctxWithoutSizeAndSizeListMapItem.bctxItemListWithoutSize,
          sizeMap: bctxKeyToBctxWithoutSizeAndSizeListMapItem.sizes.reduce((acc, size) => {
            acc[size] = {
              name: size,
              wipQty: 0,
            };
            return acc;
          }, {} as Record<string, TSizeToUserDBWipMapItem>),
          bctxKeyAndVendorId: bctxKey,
        };
        shiftMap[shiftId][bctxKey] = {
          bctxKeyAndVendorId: bctxKey,
          sizeMap: bctxKeyToBctxWithoutSizeAndSizeListMapItem.sizes.reduce((acc, size) => {
            acc[size] = {
              name: size,
              totalCompletedCount: 0,
              sentQty: 0,
            };
            return acc;
          }, {} as Record<string, TSizeToUserDBActualMapItem>),
        };
      },
    );
    const lineMapItem: TBulkCountUserDBLineMapItem = {
      bctxAndVendorWipMap,
      shiftMap,
    };
    return lineMapItem;
  },
  getMergedBulkCountDB(
    currentDailyCache: TBulkCountAppUserActualsDB['dailyCache'],
    lineMapItem: TBulkCountUserDBLineMapItem,
    selectedStationAndShiftId: TSelectedStationIdAndShiftId,
    updatedQuantitiesForOldBctx: boolean,
  ) {
    const { stationId, shiftId } = selectedStationAndShiftId;
    if (!currentDailyCache.lineMap[stationId]) {
      return produce(currentDailyCache, (currentDailyCacheDraft) => {
        currentDailyCacheDraft.lineMap[stationId] = lineMapItem;
      });
    } else {
      Object.entries(lineMapItem.bctxAndVendorWipMap).forEach(([bctxKey, item]) => {
        currentDailyCache = produce(currentDailyCache, (currentDailyCacheDraft) => {
          if (!currentDailyCacheDraft.lineMap[stationId].bctxAndVendorWipMap[bctxKey]) {
            currentDailyCacheDraft.lineMap[stationId].bctxAndVendorWipMap[bctxKey] = item;
          } else {
            Object.entries(lineMapItem.bctxAndVendorWipMap[bctxKey].sizeMap).forEach(([key, size]) => {
              if (!currentDailyCacheDraft.lineMap[stationId].bctxAndVendorWipMap[bctxKey].sizeMap[key]) {
                currentDailyCacheDraft.lineMap[stationId].bctxAndVendorWipMap[bctxKey].sizeMap[key] = size;
              } else if (updatedQuantitiesForOldBctx) {
                currentDailyCacheDraft.lineMap[stationId].bctxAndVendorWipMap[bctxKey].sizeMap[key] = size;
              }
            });
          }
        });
      });
      if (updatedQuantitiesForOldBctx) {
        currentDailyCache = produce(currentDailyCache, (draft) => {
          Object.entries(draft.lineMap[stationId].shiftMap).forEach(([shift, bctxShiftMapItem]) => {
            Object.entries(bctxShiftMapItem).forEach(([bctxKey, acutalsMap]) => {
              Object.keys(acutalsMap.sizeMap).forEach((size) => {
                if (
                  !lineMapItem.shiftMap[shift] ||
                  !lineMapItem.shiftMap[shift][bctxKey] ||
                  !lineMapItem.shiftMap[shift][bctxKey].sizeMap[size]
                ) {
                  draft.lineMap[stationId].shiftMap[shift][bctxKey].sizeMap[size].totalCompletedCount =
                    draft.lineMap[stationId].shiftMap[shift][bctxKey].sizeMap[size].sentQty;
                }
              });
            });
          });
        });
      }
      Object.entries(lineMapItem.shiftMap).forEach(([shift, item]) => {
        currentDailyCache = produce(currentDailyCache, (currentDailyCacheDraft) => {
          if (!currentDailyCacheDraft.lineMap[stationId].shiftMap[shiftId]) {
            currentDailyCache.lineMap[stationId].shiftMap[shiftId] = item;
          } else {
            Object.entries(lineMapItem.shiftMap[shift]).forEach(([bctxKey, item]) => {
              if (!currentDailyCacheDraft.lineMap[stationId].shiftMap[shiftId][bctxKey]) {
                currentDailyCacheDraft.lineMap[stationId].shiftMap[shiftId][bctxKey] = item;
              } else {
                Object.entries(lineMapItem.shiftMap[shift][bctxKey].sizeMap).forEach(([key, size]) => {
                  if (!currentDailyCacheDraft.lineMap[stationId].shiftMap[shiftId][bctxKey].sizeMap[key]) {
                    currentDailyCacheDraft.lineMap[stationId].shiftMap[shiftId][bctxKey].sizeMap[key] = size;
                  } else if (updatedQuantitiesForOldBctx) {
                    currentDailyCacheDraft.lineMap[stationId].shiftMap[shiftId][bctxKey].sizeMap[key] = size;
                  }
                });
              }
            });
          }
        });
      });
      return currentDailyCache;
    }
  },
  createInitialBulkCountReceiveAppLineMapItem(
    bctxDataResponse: TRESPONSE_WashingReceiveBctxDataResponse,
    shiftId: string,
    bctxKeysOrder: TBCTXKeysWithoutSize[],
  ) {
    const bctxAndVendorWipMap: TBulkCountUserDBLineMapItem['bctxAndVendorWipMap'] = {};
    const shiftMap: TBulkCountUserDBLineMapItem['shiftMap'] = {
      [shiftId]: {},
    };
    Object.entries(bctxDataResponse.vendorsToBctxData).forEach(([vendorId, vendorsToBctxDataItem]) => {
      vendorsToBctxDataItem.bctxData.forEach((item) => {
        const orderedBctx = BulkCountAppComputationHelper.getOrderedBctx(item.bctxWithoutSize, bctxKeysOrder);
        const bctxKeyAndVendorId = BulkCountAppComputationHelper.getUniqueBctxKeyAndVendorIdWithOrder(
          bctxKeysOrder,
          orderedBctx,
          parseInt(vendorId),
        );
        const sizeQuantities: TBulkCountUserDBShiftSizeMapItem['sizeMap'] = item.sizeCumulativeQuantities.reduce(
          (acc, sizeItem) => {
            acc[sizeItem.size] = {
              name: sizeItem.size,
              totalCompletedCount: sizeItem.cumulativeReceivedQuantity,
              sentQty: sizeItem.cumulativeSentQuantity,
            };
            return acc;
          },
          {} as TBulkCountUserDBShiftSizeMapItem['sizeMap'],
        );
        /** For receive only shiftMap is used but bctxAndVendorWipMap is also initialized to keep bctx selection logic intact */
        bctxAndVendorWipMap[bctxKeyAndVendorId] = {
          bctxItemListWithoutSize: orderedBctx,
          bctxKeyAndVendorId: bctxKeyAndVendorId,
          sizeMap: item.sizeCumulativeQuantities.reduce((acc, sizeItem) => {
            acc[sizeItem.size] = {
              name: sizeItem.size,
              wipQty: 0,
            };
            return acc;
          }, {} as TBctxAndVendorWipMapItem['sizeMap']),
        };
        shiftMap[shiftId][bctxKeyAndVendorId] = {
          bctxKeyAndVendorId,
          sizeMap: sizeQuantities,
        };
      });
    });
    const lineMapItem: TBulkCountUserDBLineMapItem = {
      bctxAndVendorWipMap,
      shiftMap,
    };
    return lineMapItem;
  },
};
